/**
 * ALTIORA · Toast helper — centralised notification wrapper on top of `sonner`.
 *
 * Provides a consistent visual language, French default messages, keyboard
 * dismiss support and semantic ARIA roles. Never call `toast()` directly
 * from a page — always route through `altioraToast.*` so that a single
 * upgrade of the notification system propagates everywhere.
 *
 *   import { altioraToast } from "@/components/altiora/toast";
 *   altioraToast.success("Client enregistré");
 *   altioraToast.error("Impossible de sauvegarder", { description: err.message });
 *   const id = altioraToast.loading("Traitement en cours…");
 *   altioraToast.dismiss(id);
 */
import { toast } from "sonner";

const BASE_OPTS = {
  duration: 4200,
  className: "altiora-toast",
};

export const altioraToast = {
  success(message, opts = {}) {
    return toast.success(message, { ...BASE_OPTS, ...opts });
  },
  error(message, opts = {}) {
    return toast.error(message, {
      ...BASE_OPTS, duration: 6000, ...opts,
    });
  },
  info(message, opts = {}) {
    return toast(message, { ...BASE_OPTS, ...opts });
  },
  warning(message, opts = {}) {
    return toast.warning(message, { ...BASE_OPTS, ...opts });
  },
  loading(message = "Chargement…", opts = {}) {
    return toast.loading(message, { ...BASE_OPTS, duration: Infinity, ...opts });
  },
  dismiss(id) { toast.dismiss(id); },
  promise(promise, msgs) {
    return toast.promise(promise, {
      loading: msgs?.loading || "Traitement en cours…",
      success: msgs?.success || "Terminé",
      error:   msgs?.error   || "Échec de l'opération",
    });
  },
};

// Convenience helpers for common patterns used across the app.
export function toastApiError(err, fallback = "Une erreur est survenue") {
  const detail = err?.response?.data?.detail;
  const msg =
    typeof detail === "string" ? detail
    : Array.isArray(detail) && detail.length ? (detail[0]?.msg || fallback)
    : (err?.message && !err.message.includes("Network") ? err.message : fallback);
  return altioraToast.error(msg);
}
